import { z } from 'zod'
import { withQuery, joinURL, type QueryObject, type QueryValue } from 'ufo'

/**
 * Strip non-numeric characters (except dots and minus) from a string before coercing to number.
 * Handles values like "1,000" or "1 000" that arrive via URL params.
 * A trailing comma + exactly 2 digits (e.g. "200,00") is treated as a decimal separator.
 */
export const stripNonNumeric = (val: unknown) => {
  if (val == null || val === '') return val
  let str = String(val)
  str = str.replace(/,(\d{2})$/, '.$1')
  return str.replace(/[^\d.\-]/g, '')
}

/**
 * An amount from a link — a refresh CTA, or a personal link minted from CRM data, which carry
 * `250`, `200,00` and `1 000`. Anything digits-and-separators still parses exactly as
 * `stripNonNumeric` parsed it; anything else is `undefined` rather than invented, where that
 * helper deleted the offending characters and returned `1e400` as 1400.
 *
 * Looser than the shape a signed mandate should use, on purpose: Mysoft's `Belopp` is "decimal
 * as string" and nothing records its exact form, so the membership card is not made to guess.
 */
const AMOUNT_SHAPE = /^[\d\s., ]+$/

export const parseAmountParam = (val: unknown): number | undefined => {
  const raw = String(firstValue(val) ?? '').trim()
  if (!raw || !AMOUNT_SHAPE.test(raw)) return undefined
  const parsed = Number(stripNonNumeric(raw))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

/**
 * A repeated query param arrives as an array, and `?x=A&x=B` must not become "A,B". The first
 * value with content wins: on a campaign link that is the code the campaign published, and
 * skipping the empties keeps `?sum=&sum=500` worth 500 rather than 0. Never fall back to
 * `values[0]` — vue-router yields `null` for a bare `?x`, and `String(null)` would hand the
 * CRM the literal code "null".
 */
const firstOf = <T>(values: T[]): T =>
  values.find(v => v !== '' && v !== null && v !== undefined) ?? ('' as T)

// Recurses: ufo's QueryObject permits a nested array, and returning one would let `withQuery`
// re-emit the repeated key this exists to collapse. Descend before judging emptiness — an
// element can be a nested array that is itself empty, and treating that container as content
// would return '' and discard a real value sitting after it.
export const firstValue = (val: unknown): unknown => {
  if (!Array.isArray(val)) return val
  for (const entry of val) {
    const leaf = firstValue(entry)
    if (leaf !== '' && leaf !== null && leaf !== undefined) return leaf
  }
  return ''
}

/**
 * Collapse every repeated param in a route query to a single value. ufo keeps duplicates
 * on purpose — `parseQuery` yields an array and `withQuery` re-emits it as repeated keys —
 * so this is the one step it will not do for us.
 *
 * Pair it with `withQuery`, never `new URLSearchParams`: that constructor turns an absent
 * value into the literal string "undefined".
 */
export const firstValues = (query: QueryObject): QueryObject =>
  Object.fromEntries(
    Object.entries(query).map(([key, value]) => [key, firstValue(value) as QueryValue]),
  )

/**
 * Build every URL param out of this: an `.extend()` redeclaring a field the plain way puts
 * the comma-join back for that field alone.
 *
 * The array branch must stay first — tried second, a coercing schema matches the array itself
 * and stringifies it straight back to "A,B". Its elements are nullish because vue-router gives
 * `null` for a bare `?x`, so `?x&x=B` arrives as `[null, 'B']`. For the same reason this suits
 * only schemas that leave '' empty: the collapse runs after the element schema, so a coercion
 * turning '' into something truthy has to pick its value first (see `sum`, `qsFlag`).
 */
export const qs = <T>(schema: z.ZodType<T, T>) =>
  z
    .array(schema.nullish())
    // `firstOf` substitutes '' rather than returning a nullish element, so the pipe always
    // receives a value the schema can judge. The cast only tells zod that T is not a
    // promise, which `z.ZodType<T, T>` already implies for every schema we pass.
    .transform(values => firstOf(values) as T)
    .pipe(schema as z.ZodType<T, Awaited<T>>)
    .or(schema)

export const qsString = () => qs(z.string())

// CRM-minted links pad values with U+2009, which `String.prototype.trim` strips. An id is never
// meant to carry whitespace, so trim it rather than look up `" 1343… "` in the CRM (#2362).
const qsId = () => qs(z.string().trim())

// Some CRM-minted links drop the `=` after whId (#2362), so the id arrives glued into a valueless
// key: `?whId134442030197&sum=75`. Read only when there is no real whId and exactly one distinct
// such id, so an ambiguous link can't pick the wrong contact. Every value is checked, not just the
// first: vue-router hands a repeated key over as an array, URLSearchParams as separate entries.
const GLUED_WHID = /^whId(\d+)$/

const hasText = (value: unknown): boolean =>
  Array.isArray(value) ? value.some(hasText) : String(value ?? '').trim() !== ''

export const gluedWhId = (entries: Iterable<[string, unknown]>): string | undefined => {
  const candidates = new Set<string>()
  for (const [key, value] of entries) {
    if (key === 'whId' && hasText(value)) return undefined
    const match = GLUED_WHID.exec(key.trim())
    if (match?.[1] && !hasText(value)) candidates.add(match[1])
  }
  return candidates.size === 1 ? [...candidates][0] : undefined
}

/**
 * A flag is genuinely loose — it reads an explicit "false"/"0" as false rather than as a
 * non-empty string — and it coerces, so it picks its value before parsing rather than after.
 */
export const qsFlag = () =>
  z.preprocess(val => {
    const first = firstValue(val)
    return first === 'false' || first === '0' ? false : first
  }, z.coerce.boolean())

const zLang = z.enum(['sv', 'en', 'es', 'uk', 'fi', ''])
const zResultState = z.enum(['ok', 'aborted', 'postal'])
const zCurrency = z.enum(['sek', 'eur'])

// Some result links carry a currency in the wrong case, padded or empty. Anything unreadable falls
// back to sek rather than failing the page.
const normaliseCurrency = (val: unknown): unknown => {
  if (Array.isArray(val)) return val.map(normaliseCurrency).filter(v => v !== '')
  if (typeof val === 'string') return val.trim().toLowerCase()
  return val
}

/**
 * Url param zod schemas
 */

export const zQueryParams = z.object({
  // language override
  lang: qs(zLang).nullish().default(''),

  // Campaign overrides
  campaignCode: qsString().nullish().prefault(''), // campaignCode override (salesforce or Dynamics)
  askCode: qsString().nullish().prefault(''), // second ask campaignCode override (salesforce or Dynamics)

  // Tools
  debug: qsFlag().prefault(false), // Displays debug info, urls, forces sum to minimum amount
  preview: qsFlag().prefault(false), // Preview mode

  // Form data presets
  // `parseAmountParam` picks its own value, because it must do so before parsing: coercion turns
  // '' into 0, so collapsing afterwards would read `?sum=&sum=500` as 0 rather than 500.
  // `.optional()` sits outside so the key is optional to a caller; preprocess alone types its
  // input as `unknown`, which TypeScript reads as required.
  sum: z.preprocess(parseAmountParam, z.number().nullish()).optional(),
  ssn: qsString().nullish().prefault(''),
  birthDate: qsString().nullish().prefault(''),
  street: qsString().nullish().prefault(''),
  postcode: qsString().nullish().prefault(''),
  city: qsString().nullish().prefault(''),
  countryCode: qsString().nullish().prefault(''),
  mobile: qsString().nullish().prefault(''),
  email: qsString().nullish().prefault(''),
  fname: qsString().nullish().prefault(''),
  lname: qsString().nullish().prefault(''),
  whId: qsId().nullish().prefault(''), // Dynamics websafe id (hashId)

  // Google style tracking params -- these are actively passed on
  utm_source: qsString().nullish().prefault(''),
  utm_medium: qsString().nullish().prefault(''),
  utm_campaign: qsString().nullish().prefault(''),
  utm_term: qsString().nullish().prefault(''),
  utm_content: qsString().nullish().prefault(''),
  utm_id: qsString().nullish().prefault(''),
})

export const zFormUrls = zQueryParams.extend({
  step: qsString().nullish().prefault(''),
  handedOut: qsFlag().prefault(false),
})

export const zResultUrlsType = z.enum([
  'petition',
  'protest',
  'company-single',
  'company-monthly',
  'donation-single',
  'donation-gifted',
  'donation-memorial',
  'donation-monthly',
  'membership-annual',
  'membership-annual-gifted',
  'membership-monthly',
  'rdk',
  'invoice', // including renewal
  'event',
  'tack',
  'gracias',
])
export type ResultUrlsTypeEnum = z.infer<typeof zResultUrlsType>

export const zResultUrls = zQueryParams.extend({
  // Tracking params
  type: qs(zResultUrlsType).optional(), // Interaction type
  slug: qsString().nullish().prefault(''), // Contentful form reference slug
  formId: qsString().nullish().prefault(''), // The form's id: Drupal's readable id, or `cf:<slug>` for Contentful
  formLang: qsString().nullish().prefault(''), // The form document's language; a form is (formId, formLang)
  from: qsString().nullish().prefault(''), // Path of the page the form was on, for the thank-you's share link
  formSlug: qsString().nullish().prefault(''), // A Drupal petition document's slug, when `slug` keeps the legacy one
  resultSlug: qsString().optional(), // Override the result-page path with a host's own
  // localized, same-site slug (e.g. tack / gracias / diakuiu) instead of the default
  // /thank-you | /tack. Control param only — never emitted into the query string.
  debug: qsString().optional(), // Displays debug info, urls, forces sum to minimum amount

  // Common params
  rt: qsString().nullish().prefault(''), // Sealed result token — carries who the person is
  wsId: qsString().nullish().prefault(''), // Salesforce websafe id
  whId: qsId().nullish().prefault(''), // Dynamics websafe id (hashId)
  sysId: qsString().nullish().prefault(''), // Contentful result page sysId to load
  state: qs(zResultState).optional().default('ok'), // Result state

  // Djurens Rätt params
  partId: qsString().nullish().prefault(''), // Dynamics user partId for Djurens Rätt
  transactionId: qsString().nullish().prefault(''), // Dynamics transaction id

  // 1882 Params
  webOrderId: qsString().nullish().prefault(''), // Salesforce web order id
  customerId: qsString().nullish().prefault(''), // Stripe customer id
  invoiceId: qsString().nullish().prefault(''), // Stripe invoice id

  currency: z.preprocess(normaliseCurrency, qs(zCurrency).catch('sek')).optional().default('sek'),
})

/**
 * The params a result URL must never carry. Tracking pixels record the full page URL, so a
 * supporter's details in the query reach Meta and Google — which is how Meta came to block
 * `receiverDob` on the Rädda Djuren-klubben flow.
 *
 * Who the person is travels as an opaque id the server resolves instead: `rt` (the sealed result
 * token) or `whId` (the Dynamics websafe id, which interact's `/api/contact` reads).
 *
 * `buildResultUrl` does not drop these on its own — a caller that still needs a name in the URL
 * has to be migrated first rather than silently broken — so run inbound query params through
 * `omitPersonalParams` at the call site.
 */
export const PERSONAL_RESULT_PARAMS = [
  'fname',
  'lname',
  'email',
  'mobile',
  'countryCode',
  'city',
  'street',
  'postcode',
  'ssn',
  'birthDate',
] as const

/** Drop `PERSONAL_RESULT_PARAMS` from a query object, leaving every other key untouched. */
export const omitPersonalParams = <T extends Record<string, unknown>>(query: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([key]) => !(PERSONAL_RESULT_PARAMS as readonly string[]).includes(key),
    ),
  ) as Partial<T>

/**
 * The campaign and tracking params a form forwards to its result page — the allowlist for a host
 * that forwards nothing else (rdk, interact's fundraising pages), where `omitPersonalParams` is the
 * blocklist for one that forwards the rest. None of them can identify a person.
 */
export const TRACKING_RESULT_PARAMS = [
  'lang',
  'campaignCode',
  'askCode',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const

/** Keep only `TRACKING_RESULT_PARAMS` from a query object. */
export const pickTrackingParams = <T extends Record<string, unknown>>(query: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(query).filter(([key]) =>
      (TRACKING_RESULT_PARAMS as readonly string[]).includes(key),
    ),
  ) as Partial<T>

function removeEmpty(obj: { [key: string]: any }): { [key: string]: any } {
  return Object.entries(obj).reduce((a, [k, v]) => (v === '' ? a : { ...a, [k]: v }), {})
}

function removeNone(obj: { [key: string]: any }): { [key: string]: any } {
  return Object.entries(obj).reduce((a, [k, v]) => (v === 'NONE' ? a : { ...a, [k]: v }), {})
}

/**
 * Localized result/thank-you slug per language — the same-site path a host serves its
 * result page on. Pass `RESULT_SLUG[lang]` as `resultSlug` to `buildResultUrl` (with an
 * empty base) to land a form on the host's own page instead of the off-site act host.
 * sv/en/es match interact-n3's localePageMap (tack / thank-you / gracias). `uk` deliberately
 * diverges: interact-n3 reuses `/thank-you` for Ukrainian, but we give it its own slug — the
 * transliterated Ukrainian "Дякую" → `diakuiu`.
 */
const SV_RESULT_SLUG = 'tack'

export const RESULT_SLUG: Record<string, string> = {
  sv: SV_RESULT_SLUG,
  en: 'thank-you',
  es: 'gracias',
  uk: 'diakuiu',
  fi: 'kiitos',
}

/** The slug a site serves its result page on. The page and every form that redirects to
 *  it must resolve through here, or an unlisted language sends signers to a 404. */
export const resultSlugFor = (lang: string): string => RESULT_SLUG[lang] ?? SV_RESULT_SLUG

export const buildResultUrl = (
  baseUrl: string,
  params: z.input<typeof zResultUrls>,
  siteOrg?: string,
): string => {
  const parsedParams = zResultUrls.parse(params)
  let onlyDefined = removeNone(removeEmpty(parsedParams))
  delete onlyDefined['debug'] // never pass on debug
  // The token says who the person is, so no other form of identity travels alongside it — not the
  // personal params, and not the CRM ids a page would otherwise resolve the person from.
  if (onlyDefined['rt']) {
    onlyDefined = omitPersonalParams(onlyDefined)
    delete onlyDefined['whId']
    delete onlyDefined['partId']
    delete onlyDefined['wsId']
  }
  // `resultSlug` lets a host point at its own localized, same-site result page; it's a
  // control param, so pull it out before building the query string.
  const slugOverride = onlyDefined['resultSlug']
  delete onlyDefined['resultSlug']
  // Sanitise the override: it's a same-site slug only, so strip any `?query`/`#fragment` and trim
  // before it becomes the path — otherwise `tack?x=1` would land inside `withQuery`'s path segment.
  // A single path segment, or nothing. `useResultUrls` spreads `route.query` into these params,
  // so this is reachable from a URL: anything with a slash, a backslash or a dot segment could
  // otherwise leave `//evil.com` (protocol-relative, another host) or `../admin` once a browser
  // normalises it. Matching the shape rather than stripping characters means a value that is not
  // a slug is ignored and the default path is used, instead of being edited into one.
  // One leading slash is allowed and dropped, because callers pass both `tack` and `/gracias`.
  const rawSlug = String(slugOverride ?? '')
    .trim()
    .replace(/^\//, '')
  const cleanSlug = /^[a-z0-9-]+$/i.test(rawSlug) ? rawSlug : ''
  const path = cleanSlug
    ? `/${cleanSlug}`
    : !siteOrg || siteOrg !== 'raddadjuren'
      ? '/thank-you'
      : '/tack'
  const relative = withQuery(path, onlyDefined)
  return baseUrl ? joinURL(baseUrl, relative) : relative
}
